/**
 * スポットの追加・編集パネル
 *
 * 常設シートの中身として差し替えて使う。地図に出すには座標が要るので、
 * Google の場所検索から選べるようにしている。検索は無料枠を消費するため、
 * 入力中の逐次検索はせずボタンを押したときだけ呼ぶ。
 *
 * 名前横の検索は追加時限定。スポットの表示名は Place のものをそのまま使うので、
 * 編集時に名前を直す欄は要らず、場所の差し替えは ItemPlaceSection に任せる。
 */

import { Loader2, MapPin, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { searchRallyPlaces } from '@/api/services/stamp-rally'
import type { CreateItemParams, PlaceSearchResult, StampRallyItem } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import type { Coords } from '@/hooks/useCurrentPosition'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { formatDistanceMeters } from '@/lib/activity-format-utils'

import { ItemPlaceSection } from './item-place-section'

type Props = {
  onSubmit: (params: CreateItemParams) => Promise<void>
  /** 指定すると編集モードになる */
  item?: StampRallyItem | null
  /** 検索結果を近場に寄せる基準 */
  origin?: Coords | null
  /** 編集モードでのみ使う */
  onDelete?: () => Promise<void>
  /** 編集モードでのみ使う。地図から開いたときにその場でチェックできるようにする */
  onToggleStamp?: (item: StampRallyItem) => void
  /** 場所連携を変えたときに一覧を取り直す */
  onPlaceChanged?: () => void
}

export const ItemFormPanel = ({ onSubmit, item, origin, onDelete, onToggleStamp, onPlaceChanged }: Props) => {
  // 別のスポットへ切り替えるときは呼び出し側が key で作り直すので、初期値だけ見れば足りる
  const [name, setName] = useState(item?.name ?? '')
  const [address, setAddress] = useState(item?.address ?? '')
  const [externalUrl, setExternalUrl] = useState(item?.external_url ?? '')
  const [note, setNote] = useState(item?.note ?? '')
  const [coords, setCoords] = useState<Coords | null>(
    item?.latitude != null && item.longitude != null ? { latitude: item.latitude, longitude: item.longitude } : null,
  )
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(item?.google_place_id ?? null)
  const [results, setResults] = useState<PlaceSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const { isSubmitting, run } = useSubmitLock()

  const isEdit = Boolean(item)

  const handleSearch = async () => {
    const query = name.trim()
    if (!query) return

    setIsSearching(true)
    try {
      const found = await searchRallyPlaces(query, origin)
      setResults(found)
      if (found.length === 0) toast.info('場所が見つかりませんでした')
    } catch {
      toast.error('場所の検索に失敗しました')
    } finally {
      setIsSearching(false)
    }
  }

  const handlePick = (result: PlaceSearchResult) => {
    setName(result.name)
    setAddress(result.address ?? '')
    setCoords({ latitude: result.latitude, longitude: result.longitude })
    setGooglePlaceId(result.google_place_id)
    setResults([])
  }

  const handleSubmit = async () => {
    if (!name.trim()) return
    // 場所の実体を作る都合で座標が要る。検索から選ばないと登録できない
    if (!googlePlaceId || !coords) {
      toast.error('「検索」から場所を選んでください')
      return
    }
    await run(() =>
      onSubmit({
        name: name.trim(),
        address: address.trim() || null,
        external_url: externalUrl.trim() || null,
        note: note.trim() || null,
        latitude: coords.latitude,
        longitude: coords.longitude,
        google_place_id: googlePlaceId,
      }),
    )
  }

  return (
    <div className="space-y-4 px-4 pt-4 pb-6">
      {item && onToggleStamp && (
        <div className="flex items-center justify-between rounded-xl border bg-card px-3 py-3">
          <Label htmlFor="item-stamped">スタンプを獲得済みにする</Label>
          <Switch id="item-stamped" checked={Boolean(item.stamped_at)} onCheckedChange={() => onToggleStamp(item)} />
        </div>
      )}

      {/* 編集時は下の「場所」が名前も住所も兼ねる。検索口を 2 つ出すとどちらが効くのか分からなくなる */}
      {!isEdit && (
        <div className="space-y-1.5">
          <Label htmlFor="item-name">名前</Label>
          <div className="flex gap-2">
            <Input
              id="item-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="松本城"
              maxLength={200}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleSearch}
              disabled={!name.trim() || isSearching}
              aria-label="場所を検索"
              className="shrink-0"
            >
              {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {coords ? address || '位置登録済み。地図に表示されます' : '検索して選ぶと位置が付いて地図に載ります'}
          </p>
        </div>
      )}

      {results.length > 0 && (
        <div className="overflow-hidden rounded-xl border bg-card">
          {results.map((result) => (
            <button
              key={result.google_place_id ?? `${result.latitude},${result.longitude}`}
              onClick={() => handlePick(result)}
              className="flex w-full items-start gap-2 border-b border-border/50 px-3! py-2.5! text-left last:border-b-0"
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{result.name}</p>
                {result.address && <p className="truncate text-xs text-muted-foreground">{result.address}</p>}
              </div>
              {result.distance_m != null && (
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {formatDistanceMeters(result.distance_m)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* 名前と位置の変更口。保存を待たず即反映される */}
      {item && (
        <ItemPlaceSection
          item={item}
          origin={origin}
          onChanged={(result) => {
            setName(result.item.name)
            setAddress(result.place?.address ?? '')
            onPlaceChanged?.()
          }}
        />
      )}

      <div className="space-y-1.5">
        <Label htmlFor="item-url">公式ページ</Label>
        <Input
          id="item-url"
          value={externalUrl}
          onChange={(e) => setExternalUrl(e.target.value)}
          placeholder="https://..."
          inputMode="url"
          maxLength={500}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="item-note">メモ</Label>
        <Textarea
          id="item-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="スタンプ設置場所（1F 売店横 など）"
          rows={3}
        />
      </div>

      {/* 保存を待たず即反映されるので、フォーム本体とは分けて一番下に置く */}
      <Button onClick={handleSubmit} disabled={!name.trim() || isSubmitting} className="w-full">
        {item ? '保存' : '追加'}
      </Button>

      {item && onDelete && (
        <Button variant="ghost" onClick={onDelete} disabled={isSubmitting} className="w-full text-destructive">
          <Trash2 className="size-4" />
          このスポットを削除
        </Button>
      )}
    </div>
  )
}
